import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['phoneInput', 'countryCodeInput']
  static values = {
    initialCountry: String,
    preferredCountries: Array,
    locale: String
  }

  static classes = ['countryOption']

  // intl-tel-input and its translations are imported here rather than at the top of the file:
  // this controller is registered on every page but used on three forms, and a static import
  // put the library into the bundle every page loads.
  //
  // Turbo can disconnect the element while the import is in flight. The token makes a stale
  // connect bail out instead of attaching an instance that disconnect() will never destroy.
  async connect() {
    const token = (this.connection = {})
    const [{ default: intlTelInput }, { nl, de, en }] = await Promise.all([
      import('intl-tel-input'),
      import('intl-tel-input/i18n')
    ])
    if (this.connection !== token) return

    this.initializePhoneInput(intlTelInput, { nl, de, en })
  }

  disconnect() {
    this.connection = null
    if (this.iti) {
      this.iti.destroy()
      this.iti = null
    }
  }

  initializePhoneInput(intlTelInput, translations) {
    const { nl, de, en } = translations
    // Get the appropriate i18n object based on locale
    const i18nMap = {
      'nl': nl,
      'de': de,
      'en': en
    }

    const currentLocale = this.localeValue || 'en'
    const i18nObject = i18nMap[currentLocale] || en

    // countryOrder and loadUtils, not preferredCountries and utilsScript: those
    // two were renamed in intl-tel-input v24 and are silently ignored by the
    // version installed here. The effect was that utils never loaded at all, so
    // there was no country-specific placeholder, no format-as-you-type and no
    // real validation, and the preferred countries never floated to the top.
    //
    // Utils are imported from the package rather than jsdelivr. CSP script_src is
    // :self, :unsafe_inline, so the CDN fetch was logging violations and would
    // have broken outright once the policy stops being report-only.
    const options = {
      initialCountry: this.initialCountryValue || 'nl',
      countryOrder: this.preferredCountriesValue?.length ? this.preferredCountriesValue : ['nl', 'de', 'gb', 'us'],
      separateDialCode: true,
      useFullscreenPopup: false,
      formatAsYouType: true,
      loadUtils: () => import('intl-tel-input/utils'),
      i18n: i18nObject
    }

    this.iti = intlTelInput(this.phoneInputTarget, options)

    // Style dropdown on open
    const container = this.phoneInputTarget.closest('.iti')
    this.phoneInputTarget.addEventListener('open:countrydropdown', () => {
      requestAnimationFrame(() => {
        const dropdown = container.querySelector('.iti__dropdown-content')
        if (dropdown) this.styleDropdown(dropdown)
      })
    })

    // Update country code when country changes
    this.phoneInputTarget.addEventListener('countrychange', () => {
      this.updateCountryCode()
    })

    // Update country code on initial load
    this.updateCountryCode()
  }

  styleDropdown(dropdown) {
    // Style individual country options (must be done in JS as they are
    // re-rendered by the library on search). The classes come from the markup so
    // each brand supplies its own: a class named only in here sits outside every
    // Tailwind content glob and would never be compiled.
    const countries = dropdown.querySelectorAll('.iti__country')
    const classes = this.hasCountryOptionClass ? this.countryOptionClasses : ['p-4', 'rounded-lg']
    countries.forEach(country => country.classList.add(...classes))
  }

  updateCountryCode() {
    const countryData = this.iti.getSelectedCountryData()
    this.countryCodeInputTarget.value = countryData.iso2.toUpperCase()
  }

  // Method to get the full international number
  getFullNumber() {
    return this.iti.getNumber()
  }

  // Method to validate the phone number
  isValid() {
    return this.iti.isValidNumber()
  }

  // Method to set the phone number programmatically
  setNumber(number) {
    this.iti.setNumber(number)
    this.updateCountryCode()
  }
}
